import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "./i18n/LanguageContext";
import { AuthProvider } from "./hooks/useAuth";
import { SiteSettingsProvider } from "./hooks/useSiteSettings";
import ErrorBoundary from "./components/ErrorBoundary";
import ProtectedPage from "./components/ProtectedPage";
import { InstrumentListSkeleton, InstrumentDetailSkeleton, AtelierSkeleton, ContactSkeleton } from "./components/skeletons/PageSkeleton";
import Index from "./pages/Index";

const Atelier = lazy(() => import("./pages/Atelier"));
const Instruments = lazy(() => import("./pages/Instruments"));
const InstrumentDetail = lazy(() => import("./pages/InstrumentDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Impressum = lazy(() => import("./pages/Impressum"));
const Datenschutz = lazy(() => import("./pages/Datenschutz"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <LanguageProvider>
        <AuthProvider>
          <SiteSettingsProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <ErrorBoundary>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/instruments" element={
                    <Suspense fallback={<InstrumentListSkeleton />}>
                      <ProtectedPage settingsKey="page_instruments_visible">
                        <Instruments />
                      </ProtectedPage>
                    </Suspense>
                  } />
                  <Route path="/instruments/:slug" element={
                    <Suspense fallback={<InstrumentDetailSkeleton />}>
                      <ProtectedPage settingsKey="page_instruments_visible">
                        <InstrumentDetail />
                      </ProtectedPage>
                    </Suspense>
                  } />
                  <Route path="/atelier" element={
                    <Suspense fallback={<AtelierSkeleton />}>
                      <ProtectedPage settingsKey="page_atelier_visible">
                        <Atelier />
                      </ProtectedPage>
                    </Suspense>
                  } />
                  {/* /about removed — canonical URL is /atelier */}
                  <Route path="/contact" element={
                    <Suspense fallback={<ContactSkeleton />}>
                      <ProtectedPage settingsKey="page_contact_visible">
                        <Contact />
                      </ProtectedPage>
                    </Suspense>
                  } />
                  <Route path="/impressum" element={
                    <Suspense fallback={null}>
                      <ProtectedPage settingsKey="page_impressum_visible">
                        <Impressum />
                      </ProtectedPage>
                    </Suspense>
                  } />
                  <Route path="/datenschutz" element={
                    <Suspense fallback={null}>
                      <ProtectedPage settingsKey="page_datenschutz_visible">
                        <Datenschutz />
                      </ProtectedPage>
                    </Suspense>
                  } />
                  <Route path="/admin-login" element={
                    <Suspense fallback={null}><AdminLogin /></Suspense>
                  } />
                  <Route path="/admin" element={
                    <Suspense fallback={null}><AdminDashboard /></Suspense>
                  } />
                  <Route path="/unsubscribe" element={
                    <Suspense fallback={null}><Unsubscribe /></Suspense>
                  } />
                  <Route path="*" element={
                    <Suspense fallback={null}><NotFound /></Suspense>
                  } />
                </Routes>
              </ErrorBoundary>
            </BrowserRouter>
          </SiteSettingsProvider>
        </AuthProvider>
      </LanguageProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
